import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { autocompleteClasses, Box, IconButton, Paper, Theme, Typography } from '@mui/material'
import { CheckPoint } from 'interface'
import { DivIcon, Icon, IconOptions, Map } from 'leaflet'
import { useEffect, useState } from 'react'
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from 'react-leaflet'
import './leaflet.css'
import { iconChecked, iconCheckpoint, iconLocation } from './MakeIcon'
import { useNavigate } from 'react-router-dom'
import { number } from 'yup'
import DialogQuiz from 'components/Dialogs/DialogQuiz'
import { convertLength } from '@mui/material/styles/cssUtils'

const zoom = 17

interface LocationProps {
  icon?: Icon<IconOptions> | DivIcon
  position?: { lat: number; lng: number }
  center?: boolean
  checkpoint?: CheckPoint
  setPosition?: (position: { lat: number; lng: number }) => void
}

function LocationMarker({
  position,
  center = false,
  checkpoint,
  icon = iconCheckpoint,
}: LocationProps) {
  const map = useMapEvents({})
  useEffect(() => {
    if (center && position) {
      map.flyTo(
        {
          lat: position.lat,
          lng: position.lng,
        },
        zoom
      )
    }
  }, [center, map, position])

  return position ? (
    <Marker position={position} icon={icon}>
      {checkpoint && (
        <Popup closeOnClick position={position} offset={[0, -40]}>
          {checkpoint.name}
        </Popup>
      )}
    </Marker>
  ) : null
}

interface Props {
  positions: CheckPoint[]
}
export default function GameMap({ positions }: Props) {
  const [mapContainer, setMap] = useState<Map | null>(null)
  const [center, setCenter] = useState<{ lat: number; lng: number }>()
  const [currentPos, setCurrentPos] = useState<{ lat: number; lng: number }>()
  useEffect(() => {
    getCurrentPosition().then((pos) => {
      setCenter(pos)
      setCurrentPos(pos)
    })
  }, [])
  const [nearlyCp, setNearlyCp] = useState<{
    eventId: number | null
    cpId: number | null
  }>({ eventId: null, cpId: null })
  const [loading, setLoading] = useState(false)
  const [quizDialog, setQuizDialog] = useState(false)
  const navigate = useNavigate()
  const [curRange, setCurRange] = useState(0.01)

  useEffect(() => {
    const timer = setInterval(() => {
      getCurrentPosition().then((pos) => {
        setCurrentPos(pos)
        setNearlyCp({ eventId: null, cpId: null })
        setCurRange(0.1)
        positions.map((v, i) => {
          const range =
            Math.sqrt(
              Math.pow(v.lat - pos.lat, 2) + Math.pow(v.lng - pos.lng, 2)
            )
          if (range < curRange) {
            setNearlyCp({ eventId: v.eventId, cpId: v.id })
            setCurRange(range)
          }
        })
      })
    }, 5000)
    return () => {
      clearInterval(timer)
    }
  }, [])
  const handleSubmit = async () => {
  }
  if (!center) return null
  return (
    <>
    <DialogQuiz
      open={quizDialog}
      maxWidth="sm"
      loading={loading}
      handleSubmit={handleSubmit}
      eventId={nearlyCp.eventId}
      cpId={nearlyCp.cpId}
      title="問題"
      primaryButtonText="解答を送信"
      primaryButtonColor="primary"
      handleClose={() => setQuizDialog(false)}
    >
    </DialogQuiz>
    <Paper elevation={4}>
      <Box
        borderRadius={2}
        overflow="hidden"
        width="100%"
        sx={{
          border: '2px solid',
          borderColor: '#0000',
          position: 'relative',
        }}
      >
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom={false}
          style={{ width: '100%', height: '83vh' }}
          ref={setMap}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {positions.map((v, i) => {
            return (
              <LocationMarker
                key={i}
                icon={iconChecked}
                checkpoint={v}
                position={v}
              />
            )
          })}
          <LocationMarker icon={iconLocation} position={currentPos} />
        </MapContainer>
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            zIndex: 500,
            bgcolor: (theme) => theme.palette.primary.main,
            color: (theme) => theme.palette.primary.contrastText,
            '&:hover': {
              bgcolor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.contrastText,
            },
          }}
          color="primary"
          onClick={() => {
            mapContainer?.flyTo(center, 17)
          }}
        >
          <GpsFixedIcon fontSize="medium" />
        </IconButton>
        {nearlyCp && nearlyCp.cpId ? (
          <IconButton
            sx={{
              position: 'absolute',
              bottom: 100,
              left: 0,
              right: 0,
              margin: 'auto',
              width: 70,
              height: 70,
              zIndex: 500,
              bgcolor: (theme) => theme.palette.secondary.main,
              color: (theme) => theme.palette.secondary.contrastText,
              '&:hover': {
                bgcolor: (theme) => theme.palette.secondary.main,
                color: (theme) => theme.palette.secondary.contrastText,
              },
            }}
            color="primary"
            onClick={() =>
              setQuizDialog(true)
            }
          >
            <Typography
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                margin: 'auto',
                width: 70,
                height: 70,
                zIndex: 499,
                bgcolor: (theme: Theme) => theme.palette.secondary.main,
                color: (theme: Theme) => theme.palette.secondary.contrastText,
              }}
              color="primary"
              className="animate-ping rounded-full"
            >
            </Typography>
            <QuestionMarkIcon fontSize="large" />
          </IconButton>
        ) : (
          ''
        )}
      </Box>
    </Paper>
    </>
  )
}
function getCurrentPosition() {
  return new Promise<{
    lat: number
    lng: number
  }>((rs, rj) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          rs(pos)
        },
        () => rj('error')
      )
    } else {
      rj('error')
    }
  })
}
